import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, Loader2, CheckCircle2, AlertTriangle, X, Sparkles, Eye, CalendarDays } from "lucide-react";

const POLL_MS = 500;

// Phases (kept in sync with backend) — used to render a friendly ordered timeline
const PHASES = [
  { key: "queued",                 label: "Queued" },
  { key: "parsing",                label: "Parsing file" },
  { key: "normalizing",            label: "Normalizing rows" },
  { key: "enriching_registration", label: "Fetching registration + last deposit" },
  { key: "enriching_deposits",     label: "Fetching deposit history" },
  { key: "tagging",                label: "Tagging fresh / follow-up" },
  { key: "storing",                label: "Saving to database" },
  { key: "complete",               label: "Complete" },
];

function phaseIndex(p) {
  const idx = PHASES.findIndex((x) => x.key === p);
  return idx < 0 ? 0 : idx;
}

export default function OpsUploadPage() {
  const [ctx, setCtx] = useState({ brands: [], teams: [], team: null, can_pick_team: false });
  const [autoDetect, setAutoDetect] = useState(true);
  const [brandId, setBrandId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [reportType, setReportType] = useState("RD");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [multiProgress, setMultiProgress] = useState(null); // for auto-detect multi-upload
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const inputRef = useRef();
  const pollTimer = useRef();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/ops/my-context").then((r) => {
      setCtx(r.data);
      if (r.data.brands?.length === 1) setBrandId(String(r.data.brands[0].brand_id));
      if (r.data.team?.id) setTeamId(r.data.team.id);
    });
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, []);

  // Reset preview whenever the file or mode changes
  useEffect(() => { setPreview(null); setDateSummary(null); setDateOverrides({}); }, [file, autoDetect]);

  const runPreview = async () => {
    if (!file) return toast.error("Pick a file first");
    setPreviewLoading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/ops/uploads/preview-campaigns", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Preview failed");
    } finally { setPreviewLoading(false); }
  };

  // Per-day sanity check — runs the same date parser we use for ingest and
  // shows the day-wise breakdown. Ops picked this specifically to spot
  // Excel-locale corruption (e.g. July dates landing on Jan 7) BEFORE the
  // full ingest goes through.
  const [dateSummary, setDateSummary] = useState(null);
  const [dateSummaryLoading, setDateSummaryLoading] = useState(false);
  // Manual date corrections: { "YYYY-MM-DD": "YYYY-MM-DD" } — parsed → corrected.
  // Rebuilt from the preview UI and sent through to preview-dates AND /upload
  // so the fix propagates all the way to attempt_date_ist and deposit-window math.
  const [dateOverrides, setDateOverrides] = useState({});
  // Auto-fix Excel US-locale corruption (default ON — this is the calling
  // system's known corruption pattern). Ops can toggle off for files from
  // exporters that store dates faithfully.
  const [usLocaleFix, setUsLocaleFix] = useState(true);

  const runDatePreview = async (overridesArg) => {
    if (!file) return toast.error("Pick a file first");
    // Guard: React `onClick={runDatePreview}` passes a SyntheticEvent as the
    // first arg — must NOT treat that as an overrides map (has DOM refs +
    // circular structure → JSON.stringify fails).
    const isPlainOverrides = overridesArg && typeof overridesArg === "object"
      && !overridesArg.nativeEvent && !overridesArg.target;
    const overrides = isPlainOverrides ? overridesArg : dateOverrides;
    setDateSummaryLoading(true);
    setDateSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("us_locale_fix", usLocaleFix ? "true" : "false");
      if (overrides && Object.keys(overrides).length > 0) {
        fd.append("date_overrides", JSON.stringify(overrides));
      }
      const r = await api.post("/ops/uploads/preview-dates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // Large files (100+ MB) take time to upload + parse — 5 min ceiling.
        timeout: 5 * 60 * 1000,
      });
      setDateSummary(r.data);
    } catch (ex) {
      // Surface the real cause so 413 / 504 / network errors don't just look
      // like a generic "failed". Show status code + any response body text.
      const status = ex?.response?.status;
      const body = ex?.response?.data;
      const detail = (body && typeof body === "object" ? body.detail : null) || (typeof body === "string" ? body.slice(0, 200) : null);
      const sizeMb = file ? (file.size / (1024 * 1024)).toFixed(1) : "?";
      let msg = "Date preview failed";
      if (status === 413) {
        msg = `Upload rejected: file is too large (${sizeMb} MB) — proxy body-size limit exceeded. Increase client_max_body_size on the server / ingress, or export a smaller / .csv file.`;
      } else if (status === 504 || ex?.code === "ECONNABORTED") {
        msg = `Timed out parsing ${sizeMb} MB — try a smaller file or a .csv export (usually 3–5× smaller than .xlsx).`;
      } else if (status) {
        msg = `Date preview failed (HTTP ${status})${detail ? ` — ${detail}` : ""}`;
      } else if (ex?.message) {
        msg = `Date preview failed — ${ex.message}`;
      }
      toast.error(msg, { duration: 12000 });
    } finally { setDateSummaryLoading(false); }
  };

  const pollProgress = async (uploadId) => {
    try {
      const r = await api.get(`/ops/uploads/${uploadId}/progress`);
      const p = r.data;
      setProgress(p);
      if (p.is_done) {
        if (p.phase === "complete") {
          const s = p.meta?.stats || {};
          toast.success(
            `Upload complete in ${p.meta?.duration_s}s`,
            {
              description:
                `${p.meta?.row_count} rows saved (${p.meta?.inserted} new, ${p.meta?.updated} updated). ` +
                `Enriched: ${s.registration_hits || 0} reg-hit, ${s.reactivated || 0} reactivated, ${s.credit_yes || 0} credit-yes.`,
              duration: 8000,
            }
          );
          setTimeout(() => nav(`/ops/uploads/${uploadId}`), 800);
        } else if (p.phase === "cancelled") {
          toast.info("Upload cancelled", { duration: 5000 });
          setBusy(false);
        } else {
          toast.error(`Upload failed`, { description: p.error || p.phase_label, duration: 12000 });
          setBusy(false);
        }
        return;
      }
      pollTimer.current = setTimeout(() => pollProgress(uploadId), POLL_MS);
    } catch (ex) {
      // network hiccup — keep trying up to a limit? For MVP: just log & retry
      pollTimer.current = setTimeout(() => pollProgress(uploadId), POLL_MS * 2);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a file to upload");
    if (autoDetect) {
      // Auto-detect mode — one call, backend spawns N background tasks
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("auto_detect", "true");
        fd.append("us_locale_fix", usLocaleFix ? "true" : "false");
        if (Object.keys(dateOverrides).length > 0) {
          fd.append("date_overrides", JSON.stringify(dateOverrides));
        }
        const r = await api.post("/ops/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const uploads = r.data.uploads || [];
        const unmatched = r.data.unmatched || [];
        if (uploads.length === 0) {
          toast.error("No rows matched a configured campaign — nothing to upload.");
          setBusy(false); return;
        }
        // Init a progress map keyed by upload_id
        const initMap = {};
        uploads.forEach((u) => { initMap[u.id] = { ...u, progress: null }; });
        setMultiProgress({ uploads: initMap, unmatched, started_at: r.data.started_at });
        toast.success(
          `Auto-split into ${uploads.length} upload(s)`,
          {
            description: unmatched.length
              ? `${unmatched.length} unmatched campaign(s) — those rows were skipped.`
              : "All rows matched a configured campaign.",
            duration: 6000,
          }
        );
        // Start polling each in parallel
        uploads.forEach((u) => pollMulti(u.id));
      } catch (ex) {
        toast.error(ex?.response?.data?.detail || "Upload failed");
        setBusy(false);
      }
      return;
    }
    // Manual mode
    if (!brandId) return toast.error("Pick a brand");
    if (ctx.can_pick_team && !teamId) return toast.error("Pick a team for this upload");
    if (!reportType) return toast.error("Pick report type (FTD or RD)");
    setBusy(true);
    setProgress({ phase: "uploading", phase_label: "Uploading file to server…", rows_done: 0, rows_total: 0 });
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("brand_id", brandId);
      fd.append("report_type", reportType);
      fd.append("us_locale_fix", usLocaleFix ? "true" : "false");
      if (teamId) fd.append("team_id", teamId);
      if (Object.keys(dateOverrides).length > 0) {
        fd.append("date_overrides", JSON.stringify(dateOverrides));
      }
      const r = await api.post("/ops/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      pollProgress(r.data.id);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Upload failed");
      setBusy(false);
      setProgress(null);
    }
  };

  const pollMulti = async (uploadId) => {
    try {
      const r = await api.get(`/ops/uploads/${uploadId}/progress`);
      setMultiProgress((prev) => {
        if (!prev) return prev;
        const next = { ...prev, uploads: { ...prev.uploads } };
        if (next.uploads[uploadId]) {
          next.uploads[uploadId] = { ...next.uploads[uploadId], progress: r.data };
        }
        return next;
      });
      if (!r.data.is_done) {
        setTimeout(() => pollMulti(uploadId), POLL_MS);
      } else {
        setMultiProgress((prev) => {
          if (!prev) return prev;
          const allDone = Object.values(prev.uploads).every((u) => u.progress?.is_done);
          if (allDone) setBusy(false);
          return prev;
        });
      }
    } catch (ex) {
      setTimeout(() => pollMulti(uploadId), POLL_MS * 2);
    }
  };

  const pct = (() => {
    if (!progress) return 0;
    if (progress.phase === "complete") return 100;
    const phaseIdx = phaseIndex(progress.phase);
    // Weight phases: base 12% per phase (7 pre-complete phases → base 84%)
    const base = phaseIdx * (100 / PHASES.length);
    const rowsTotal = progress.rows_total || 0;
    const rowsDone = progress.rows_done || 0;
    const within = rowsTotal > 0 ? (rowsDone / rowsTotal) * (100 / PHASES.length) : 0;
    return Math.min(99, Math.round(base + within));
  })();

  const failed = progress?.phase === "failed";
  const done = progress?.phase === "complete";
  const cancelled = progress?.phase === "cancelled";
  const inFlight = progress && !progress.is_done && !cancelled;
  const uploadId = progress?.upload_id;

  const cancelUpload = async () => {
    if (!uploadId) return;
    try {
      await api.post(`/ops/uploads/${uploadId}/cancel`);
      toast.info("Cancel requested — will stop at next checkpoint (a few seconds).");
    } catch (ex) {
      toast.error("Cancel failed: " + (ex?.response?.data?.detail || ex.message));
    }
  };

  const cancelMulti = async (uid) => {
    try {
      await api.post(`/ops/uploads/${uid}/cancel`);
      toast.info("Cancel requested for this upload — stops at next checkpoint.");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Cancel failed");
    }
  };

  const cancelAllMulti = async () => {
    if (!multiProgress) return;
    const ids = Object.values(multiProgress.uploads)
      .filter((u) => !u.progress?.is_done)
      .map((u) => u.id);
    if (ids.length === 0) return;
    try {
      await Promise.all(ids.map((id) => api.post(`/ops/uploads/${id}/cancel`)));
      toast.info(`Cancel requested for ${ids.length} upload(s).`);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Cancel-all failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">Ops call-report upload</h1>
        <p className="text-sm text-slate-600 mt-1">
          Upload the raw daily export from the calling software. Row 3 of the file must be the header. We&apos;ll auto-enrich each row with MySQL data (registration date, deposit history, credit decision) and stream progress live below.
        </p>
        {ctx.team && !ctx.can_pick_team && (
          <p className="text-xs text-slate-500 mt-1">Your team: <span className="font-medium">{ctx.team.name}</span></p>
        )}
      </div>

      <form onSubmit={submit} className="glass-card p-6 max-w-3xl space-y-5">
        {/* Auto-detect toggle */}
        <div className="glass-solid p-4 rounded-lg flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#0A84FF] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Label htmlFor="auto-detect" className="text-sm font-medium text-slate-900">Auto-detect from Campaign column</Label>
              <Switch id="auto-detect" checked={autoDetect} onCheckedChange={setAutoDetect}
                data-testid="ops-upload-auto-detect-toggle" />
            </div>
            <p className="text-xs text-slate-600 mt-1">
              {autoDetect
                ? "The app will look up each row's Campaign name in your configured campaigns and auto-route to the right brand + team + FTD/RD. Manual selectors are hidden."
                : "You pick brand + team + report type manually. All rows in the file go to that same combo."}
            </p>
          </div>
        </div>

        {!autoDetect && (
          <>
            {/* Team */}
            <div>
              <Label>Team</Label>
              {ctx.can_pick_team ? (
                <Select value={teamId} onValueChange={setTeamId}>
                  <SelectTrigger className="glass-input mt-1" data-testid="ops-upload-team-select">
                    <SelectValue placeholder="Pick team" />
                  </SelectTrigger>
                  <SelectContent>
                    {(ctx.teams || []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="glass-input mt-1 px-3 py-2 text-sm text-slate-700" data-testid="ops-upload-team-locked">
                  {ctx.team?.name || <span className="text-amber-700">No team assigned — ask an admin.</span>}
                </div>
              )}
            </div>

            {/* Brand */}
            <div>
              <Label>Brand</Label>
              <Select value={brandId} onValueChange={setBrandId}>
                <SelectTrigger className="glass-input mt-1" data-testid="ops-upload-brand-select">
                  <SelectValue placeholder="Pick brand" />
                </SelectTrigger>
                <SelectContent>
                  {ctx.brands.map((b) => (
                    <SelectItem key={b.brand_id} value={String(b.brand_id)}>
                      {b.brand_name} — {b.currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {ctx.brands.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">No brands assigned to you. Ask an admin.</p>
              )}
            </div>

            {/* Report type */}
            <div>
              <Label>Report type</Label>
              <div className="glass-solid p-0.5 rounded-lg inline-flex text-xs mt-1" data-testid="ops-upload-report-type">
                <button
                  type="button"
                  onClick={() => setReportType("FTD")}
                  data-testid="ops-upload-report-type-ftd"
                  className={`px-4 py-1.5 rounded-md transition ${reportType === "FTD" ? "bg-white shadow font-medium text-slate-900" : "text-slate-500"}`}
                >
                  FTD (First Time Deposit)
                </button>
                <button
                  type="button"
                  onClick={() => setReportType("RD")}
                  data-testid="ops-upload-report-type-rd"
                  className={`px-4 py-1.5 rounded-md transition ${reportType === "RD" ? "bg-white shadow font-medium text-slate-900" : "text-slate-500"}`}
                >
                  RD (Re-Deposit)
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                {reportType === "FTD"
                  ? "Fresh users who registered but never deposited — credit given for first-ever deposit on/after the call."
                  : "Dormant users who deposited before — credit given if they re-deposit on/after the call."}
              </p>
            </div>
          </>
        )}

        {/* Dropzone */}
        <div
          className="glass-solid border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer"
          onClick={() => !busy && inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); if (!busy && e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]); }}
        >
          <UploadCloud className="w-8 h-8 mx-auto text-slate-500 mb-2" />
          <div className="text-sm text-slate-700">
            {file ? <span className="font-medium">{file.name}</span> : "Drop the .xls / .xlsx / .csv from the calling software"}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            data-testid="ops-upload-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>

        {file && (
          <div className="glass-solid p-3 rounded-lg flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <Label htmlFor="us-locale-fix" className="text-sm font-medium text-slate-900">
                  Auto-fix US-locale date corruption
                </Label>
                <Switch id="us-locale-fix" checked={usLocaleFix} onCheckedChange={setUsLocaleFix}
                  data-testid="us-locale-fix-toggle" />
              </div>
              <p className="text-xs text-slate-600 mt-1">
                {usLocaleFix
                  ? "ON — undoes Excel's day↔month swap for dates ≤ 12 (the calling system's known corruption pattern). Turn OFF for exporters that store dates faithfully."
                  : "OFF — dates are parsed as-is (no swap). Use only for exporters that don't round-trip through US-locale Excel."}
              </p>
            </div>
          </div>
        )}

        {autoDetect && file && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button type="button" variant="outline" onClick={runPreview} disabled={previewLoading}
                data-testid="ops-upload-preview-btn">
                {previewLoading
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analysing…</>
                  : <><Eye className="w-4 h-4 mr-2" /> Preview campaigns</>}
              </Button>
              <Button type="button" variant="outline" onClick={runDatePreview} disabled={dateSummaryLoading}
                data-testid="ops-upload-preview-dates-btn">
                {dateSummaryLoading
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Parsing dates…</>
                  : <><CalendarDays className="w-4 h-4 mr-2" /> Preview dates</>}
              </Button>
            </div>

            {preview && (
              <div className="glass-solid p-4 rounded-lg space-y-3" data-testid="campaign-preview-panel">
                <div className="text-xs text-slate-600">
                  <b>{preview.rows_with_campaign?.toLocaleString?.() || 0}</b> rows with a Campaign value out of{" "}
                  <b>{preview.total_rows?.toLocaleString?.() || 0}</b> total.
                </div>

                {preview.matched?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase font-semibold text-emerald-700 mb-1">
                      Matched — {preview.matched.length} campaign(s)
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="preview-matched-table">
                        <thead>
                          <tr className="text-slate-500">
                            <th className="text-left py-1 px-2">Campaign</th>
                            <th className="text-left py-1 px-2">Brand</th>
                            <th className="text-left py-1 px-2">Type</th>
                            <th className="text-left py-1 px-2">Team</th>
                            <th className="text-right py-1 px-2">Rows</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.matched.map((m) => (
                            <tr key={m.campaign} className="border-t border-white/60">
                              <td className="py-1 px-2 font-mono font-medium">{m.campaign}</td>
                              <td className="py-1 px-2">{m.brand_name}</td>
                              <td className="py-1 px-2">
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${m.report_type === "FTD" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>{m.report_type}</span>
                              </td>
                              <td className="py-1 px-2 text-slate-600">{m.team_name}</td>
                              <td className="py-1 px-2 text-right font-mono">{m.count.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {preview.unmatched?.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase font-semibold text-amber-700 mb-1">
                      Unmatched — {preview.unmatched.length} campaign(s) will be skipped
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="preview-unmatched-table">
                        <thead>
                          <tr className="text-slate-500">
                            <th className="text-left py-1 px-2">Campaign in file</th>
                            <th className="text-left py-1 px-2">Reason</th>
                            <th className="text-right py-1 px-2">Rows</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.unmatched.map((u, i) => (
                            <tr key={i} className="border-t border-white/60">
                              <td className="py-1 px-2 font-mono">{u.campaign}</td>
                              <td className="py-1 px-2 text-slate-600">{u.reason || "Not in configured campaigns"}</td>
                              <td className="py-1 px-2 text-right font-mono">{u.count.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">
                      Add these under <b>Campaigns</b> tab to include them in the next upload, or check for spelling mismatches.
                    </p>
                  </div>
                )}
              </div>
            )}

            {dateSummary && <DatePreviewPanel data={dateSummary} overrides={dateOverrides} setOverrides={setDateOverrides} onRepreview={runDatePreview} />}
          </div>
        )}

        {!autoDetect && file && (
          <div className="space-y-3">
            <Button type="button" variant="outline" onClick={runDatePreview} disabled={dateSummaryLoading}
              className="w-full" data-testid="ops-upload-preview-dates-btn-manual">
              {dateSummaryLoading
                ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Parsing dates…</>
                : <><CalendarDays className="w-4 h-4 mr-2" /> Preview dates</>}
            </Button>
            {dateSummary && <DatePreviewPanel data={dateSummary} overrides={dateOverrides} setOverrides={setDateOverrides} onRepreview={runDatePreview} />}
          </div>
        )}

        <Button
          type="submit"
          disabled={busy || !file}
          className="btn-tactile"
          data-testid="ops-upload-submit"
          style={{ background: "linear-gradient(135deg,#0A84FF,#0069cc)" }}
        >
          {busy ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing…</>) : (autoDetect ? "Upload & auto-route" : "Upload & enrich")}
        </Button>
      </form>

      {/* Live progress panel */}
      {progress && (
        <div className="glass-card p-5 max-w-2xl space-y-3" data-testid="ops-upload-progress">
          <div className="flex items-center gap-2">
            {done ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            ) : failed || cancelled ? (
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            ) : (
              <Loader2 className="w-5 h-5 animate-spin text-[#0A84FF]" />
            )}
            <div className="font-medium text-slate-900" data-testid="ops-upload-progress-label">
              {progress.phase_label || progress.phase}
            </div>
            <div className="ml-auto flex items-center gap-3">
              <div className="text-xs text-slate-500">
                {progress.rows_total > 0 && `${progress.rows_done || 0} / ${progress.rows_total} rows`}
              </div>
              {inFlight && (
                <button
                  type="button"
                  onClick={cancelUpload}
                  data-testid="ops-upload-cancel"
                  className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
              )}
            </div>
          </div>

          {/* Bar */}
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${failed ? "bg-amber-500" : done ? "bg-emerald-500" : "bg-[#0A84FF]"}`}
              style={{ width: `${pct}%` }}
              data-testid="ops-upload-progress-bar"
            />
          </div>

          {/* Phase timeline */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[10px] pt-1">
            {PHASES.slice(0, 7).map((p, i) => {
              const cur = phaseIndex(progress.phase);
              const state = failed && i === cur ? "fail" : i < cur || done ? "done" : i === cur ? "active" : "todo";
              return (
                <div
                  key={p.key}
                  className={
                    state === "done" ? "text-emerald-700 font-medium"
                    : state === "active" ? "text-[#0A84FF] font-semibold"
                    : state === "fail" ? "text-amber-700 font-semibold"
                    : "text-slate-400"
                  }
                >
                  {state === "done" ? "✓ " : state === "active" ? "▸ " : state === "fail" ? "⚠ " : "· "}
                  {p.label}
                </div>
              );
            })}
          </div>

          {failed && progress.error && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded">
              {progress.error}
            </div>
          )}
        </div>
      )}

      {/* Multi-upload progress (auto-detect mode) */}
      {multiProgress && (
        <div className="glass-card p-5 max-w-3xl space-y-3" data-testid="ops-upload-multi-progress">
          <div className="flex items-center justify-between gap-2">
            <div className="font-medium text-slate-900">
              Auto-split into {Object.keys(multiProgress.uploads).length} upload(s)
            </div>
            {Object.values(multiProgress.uploads).some((u) => !u.progress?.is_done) && (
              <button type="button" onClick={cancelAllMulti}
                data-testid="ops-upload-multi-cancel-all"
                className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition flex items-center gap-1">
                <X className="w-3 h-3" /> Cancel all remaining
              </button>
            )}
          </div>
          {multiProgress.unmatched?.length > 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Skipped {multiProgress.unmatched.reduce((s, u) => s + u.count, 0)} row(s) across {multiProgress.unmatched.length} unmatched campaign(s):{" "}
              <span className="font-mono">{multiProgress.unmatched.map((u) => `${u.campaign}(${u.count})`).join(", ")}</span>
            </div>
          )}
          <div className="space-y-2">
            {Object.values(multiProgress.uploads).map((u) => {
              const p = u.progress;
              const isDone = p?.is_done && p?.phase === "complete";
              const isFail = p?.is_done && p?.phase !== "complete" && p?.phase !== "cancelled";
              const phaseIdx = p ? phaseIndex(p.phase) : 0;
              const pct = isDone ? 100 : Math.round(((phaseIdx + (p?.rows_total ? (p.rows_done / p.rows_total) : 0)) / PHASES.length) * 100);
              return (
                <div key={u.id} className="glass-solid p-3 rounded" data-testid={`multi-upload-${u.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {isDone ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        : isFail ? <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                        : <Loader2 className="w-4 h-4 animate-spin text-[#0A84FF] shrink-0" />}
                      <div className="text-sm font-medium truncate">
                        {u.brand_name} <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full ml-1 bg-slate-100">{u.report_type}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <div className="text-xs text-slate-500">
                        {u.row_count} rows · {p?.phase_label || "queued"}
                      </div>
                      {!p?.is_done && p?.phase !== "cancelled" && (
                        <button
                          type="button"
                          onClick={() => cancelMulti(u.id)}
                          data-testid={`multi-upload-cancel-${u.id}`}
                          className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 flex items-center gap-1"
                        >
                          <X className="w-3 h-3" /> Cancel
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#0A84FF] to-[#0069cc] transition-all"
                      style={{ width: `${Math.min(100, Math.max(3, pct))}%` }} />
                  </div>
                  {isDone && (
                    <div className="mt-1">
                      <button onClick={() => nav(`/ops/uploads/${u.id}`)}
                        className="text-[11px] text-[#0A84FF] underline"
                        data-testid={`multi-upload-open-${u.id}`}>Open →</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Date preview result panel ------------------------------------------
// Renders the per-day breakdown from /uploads/preview-dates so ops can eyeball
// if the excel-locale corruption (e.g. July dates landing on Jan 7) is in play
// BEFORE they commit to a real upload.
function DatePreviewPanel({ data, overrides = {}, setOverrides, onRepreview }) {
  if (!data) return null;
  const { total_rows = 0, parsed = 0, unparsed = 0, unparsed_samples = [], days = [], date_range = [null, null], warnings = [], overrides_applied = {} } = data;
  const [from, to] = date_range || [null, null];
  const maxCount = days.reduce((m, d) => Math.max(m, d.count || 0), 0) || 1;
  const canEdit = typeof setOverrides === "function";
  // Only count real overrides (parsed → different date). Same-date entries
  // are informational — they wouldn't change anything on Upload.
  const effectiveOverrides = Object.fromEntries(
    Object.entries(overrides || {}).filter(([k, v]) => v && v !== k)
  );
  const overrideCount = Object.keys(effectiveOverrides).length;

  const setOverride = (parsedDate, correctedDate) => {
    if (!canEdit) return;
    // No-op guard: if user picks the same date, treat as a remove so we
    // don't clutter the "Manual corrections queued" list with 1:1 entries
    // that produce no change on Upload.
    if (correctedDate === parsedDate) {
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[parsedDate];
        return next;
      });
      return;
    }
    setOverrides((prev) => ({ ...prev, [parsedDate]: correctedDate }));
  };
  const removeOverride = (parsedDate) => {
    if (!canEdit) return;
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[parsedDate];
      return next;
    });
  };
  const clearAll = () => canEdit && setOverrides({});

  return (
    <div className="glass-solid p-4 rounded-lg space-y-3" data-testid="date-preview-panel">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
        <span><b>{total_rows.toLocaleString()}</b> total rows</span>
        <span className="text-emerald-700"><b>{parsed.toLocaleString()}</b> parsed</span>
        {unparsed > 0 && <span className="text-amber-700"><b>{unparsed.toLocaleString()}</b> unparsed</span>}
        {from && to && (
          <span className="ml-auto text-slate-700">
            Range: <b className="font-mono">{from}</b> → <b className="font-mono">{to}</b>
          </span>
        )}
      </div>

      {warnings.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 space-y-2" data-testid="date-preview-warnings">
          <div className="text-xs font-semibold text-red-800 uppercase tracking-wide">
            ⚠ Suspicious date cluster detected ({warnings.length})
          </div>
          {warnings.map((w, i) => (
            <div key={i} className="text-xs text-red-900 flex flex-col gap-1" data-testid={`date-warning-${w.date}`}>
              <div className="font-mono font-semibold">{w.date} — {w.count.toLocaleString()} rows ({w.pct}%)</div>
              <div>{w.message}</div>
              {canEdit && w.swap_suggested && (
                <button
                  type="button"
                  onClick={() => setOverride(w.date, w.swap_suggested)}
                  className="self-start text-[11px] px-2 py-1 mt-1 rounded bg-red-600 text-white hover:bg-red-700 font-medium"
                  data-testid={`date-warning-swap-${w.date}`}
                >
                  Apply swap: {w.date} → {w.swap_suggested}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit && (overrideCount > 0 || Object.keys(overrides_applied).length > 0) && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 text-xs space-y-2" data-testid="date-overrides-summary">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-emerald-800 uppercase tracking-wide">
              Manual corrections queued ({overrideCount})
            </span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => onRepreview && onRepreview(overrides)}
                className="text-[11px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-medium"
                data-testid="date-overrides-repreview">
                Re-preview with corrections
              </button>
              <button type="button" onClick={clearAll}
                className="text-[11px] px-2 py-1 rounded border border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                data-testid="date-overrides-clear">
                Clear all
              </button>
            </div>
          </div>
          {Object.keys(effectiveOverrides).length > 0 && (
            <ul className="space-y-0.5">
              {Object.entries(effectiveOverrides).map(([k, v]) => (
                <li key={k} className="font-mono text-[11px] text-emerald-900 flex items-center gap-2">
                  <span>{k}</span>
                  <span>→</span>
                  <span className="font-semibold">{v}</span>
                  <button type="button" onClick={() => removeOverride(k)}
                    className="text-red-600 hover:text-red-800 text-[10px] underline">remove</button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-emerald-800">
            These corrections will be applied when you click <b>Upload</b>. Time-of-day is preserved for each row.
          </p>
        </div>
      )}

      {days.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No rows had a parseable date.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="date-preview-table">
            <thead>
              <tr className="text-slate-500">
                <th className="text-left py-1 px-2">IST Date</th>
                <th className="text-left py-1 px-2 w-full">Rows</th>
                <th className="text-right py-1 px-2">Count</th>
                <th className="text-left py-1 px-2">Raw types</th>
                <th className="text-left py-1 px-2">Sample raw value</th>
                {canEdit && <th className="text-left py-1 px-2">Correct to</th>}
              </tr>
            </thead>
            <tbody>
              {days.map((d) => {
                const isWarn = warnings.some((w) => w.date === d.date);
                const currentOverride = (overrides || {})[d.date];
                return (
                  <tr key={d.date} className={`border-t border-white/60 ${isWarn ? "bg-red-50" : ""}`}>
                    <td className={`py-1 px-2 font-mono ${isWarn ? "text-red-800 font-semibold" : ""}`}>
                      {isWarn && <span className="mr-1">⚠</span>}
                      {d.date}
                    </td>
                    <td className="py-1 px-2">
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${isWarn ? "bg-red-500" : "bg-[#0A84FF]"}`}
                          style={{ width: `${Math.max(3, Math.round((d.count / maxCount) * 100))}%` }} />
                      </div>
                    </td>
                    <td className="py-1 px-2 text-right font-mono">{d.count.toLocaleString()}</td>
                    <td className="py-1 px-2 font-mono text-[10px] text-slate-600">
                      {Object.entries(d.raw_types || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}
                    </td>
                    <td className="py-1 px-2 font-mono text-[10px] text-slate-500 truncate max-w-[240px]" title={(d.samples || []).join("\n")}>
                      {(d.samples || [])[0] || "—"}
                    </td>
                    {canEdit && (
                      <td className="py-1 px-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="date"
                            value={currentOverride || ""}
                            onChange={(e) => e.target.value ? setOverride(d.date, e.target.value) : removeOverride(d.date)}
                            className="glass-input h-6 px-1.5 text-[10px] font-mono"
                            data-testid={`date-override-input-${d.date}`}
                          />
                          {currentOverride && (
                            <button type="button" onClick={() => removeOverride(d.date)}
                              className="text-red-500 hover:text-red-700 text-[10px]" title="Clear correction">
                              ✕
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {unparsed > 0 && unparsed_samples.length > 0 && (
        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          <span className="font-semibold">Unparsed samples:</span>{" "}
          <span className="font-mono">{unparsed_samples.join(" · ")}</span>
        </div>
      )}

      <p className="text-[10px] text-slate-500">
        <b>Raw types</b> shows what Excel/pandas delivered per row: <code className="font-mono">datetime-cell</code> = date-typed
        cell (Excel already committed day/month interpretation — <b>if wrong, use the &quot;Correct to&quot; column to remap</b>);
        <code className="font-mono">string</code> = text cell (parser applies dayfirst=True, treats <span className="font-mono">1/7/2026</span> as 1 July).
      </p>
    </div>
  );
}
